import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, CalendarDays, Copy, Users } from "lucide-react";
import { Input, Area, MediaBlock, GalleryField, ProgramsEditor, DocumentsEditor, ActionsEditor, PublishControls, QuestionsEditor, DEFAULT_GALLERY_SETTINGS } from "./fields";
import { FrameEditor } from "./FrameEditor";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";
import { CandidaciesPanel } from "./CandidaciesPanel";
import { effectiveState } from "@/lib/publish";

const uid = (p) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const remaining = (s) => Math.max(0, (Number(s.totalSpots) || 0) - (Number(s.takenSpots) || 0));

export const AdminFormations = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(null);
  const items = value.items || [];
  const setField = (k, v) => onChange({ ...value, [k]: v });
  const setItems = (arr) => onChange({ ...value, items: arr });
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; setItems(c); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const add = () => {
    setItems([...items, { id: uid("formation"), slug: `formation-${items.length + 1}`, title: "Nouvelle formation", description: "", presentation: "", mediaType: "image", image: "", videoUrl: "", gallery: [], gallerySettings: { ...DEFAULT_GALLERY_SETTINGS }, programs: [], documents: [], level: "", duration: "", price: "", sessions: [], status: "published", publishStatus: "available", order: items.length }]);
    setOpen(items.length);
  };
  const duplicate = (i) => {
    const copy = JSON.parse(JSON.stringify(items[i]));
    copy.id = uid("formation");
    copy.slug = `${items[i].slug || "formation"}-copie`;
    copy.title = `${items[i].title || "Formation"} (copie)`;
    (copy.sessions || []).forEach((s) => { s.id = uid("s"); });
    (copy.programs || []).forEach((p) => { p.id = uid("prog"); });
    (copy.documents || []).forEach((d) => { d.id = uid("doc"); });
    const c = [...items]; c.splice(i + 1, 0, copy); setItems(c);
  };

  const setSessions = (i, sessions) => patch(i, { sessions });
  const addSession = (i) => setSessions(i, [...(items[i].sessions || []), { id: uid("s"), date: "", time: "", endDate: "", endTime: "", allDay: false, location: "", address: "", totalSpots: 10, takenSpots: 0 }]);
  const patchSession = (i, si, p) => { const s = [...(items[i].sessions || [])]; s[si] = { ...s[si], ...p }; setSessions(i, s); };

  // Fiche dédiée : formulaire complet d'UNE formation (modification isolée)
  const renderForm = (i) => {
    const it = items[i];
    if (!it) return null;
    return (
      <>
        <PublishControls item={it} onChange={(p) => patch(i, p)} testid={`formation-publish-${i}`} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Titre" value={it.title} onChange={(v) => patch(i, { title: v })} />
          <Input label="Niveau (ex: Débutant)" value={it.level} onChange={(v) => patch(i, { level: v })} />
          <Input label="Durée (ex: 1 journée)" value={it.duration} onChange={(v) => patch(i, { duration: v })} />
          <Input label="Tarif (ex: Gratuit)" value={it.price} onChange={(v) => patch(i, { price: v })} />
        </div>
        <Area label="Description (résumé court)" value={it.description} onChange={(v) => patch(i, { description: v })} aiContext="la description courte d'une formation" />
        <Area label="Présentation détaillée (page formation)" value={it.presentation} onChange={(v) => patch(i, { presentation: v })} rows={5} aiContext="la présentation détaillée d'une formation" />
        <div className="pt-4 border-t border-white/10"><ActionsEditor items={it.actions} onChange={(v) => patch(i, { actions: v })} /></div>
        <MediaBlock item={it} onPatch={(p) => patch(i, p)} />
        <FrameEditor label="Cadrage — vignette (carte)" src={it.image} value={it.imageFrame} onChange={(v) => patch(i, { imageFrame: v })} aspect="video" testid={`formation-frame-card-${i}`} hint="Recadrez l'image telle qu'elle apparaît sur les cartes de formations." />
        {(it.mediaType === "image" || !it.mediaType) && (
          <FrameEditor label="Cadrage — grande image (fiche)" src={it.image} value={it.detailFrame} onChange={(v) => patch(i, { detailFrame: v })} aspect="video" testid={`formation-frame-detail-${i}`} hint="Cadrage indépendant pour la grande image en haut de la fiche formation." />
        )}
        <GalleryField label="Galerie de médias (photos & vidéos — page formation)" items={it.gallery || []} settings={it.gallerySettings}
          onItemsChange={(v) => patch(i, { gallery: v })} onSettingsChange={(v) => patch(i, { gallerySettings: v })} testid={`formation-gallery-${i}`} />
        <ProgramsEditor items={it.programs || (it.program && it.program.length ? [{ id: "prog-legacy", title: "Programme", steps: it.program }] : [])} onChange={(v) => patch(i, { programs: v })} testid={`formation-programs-${i}`} />
        <DocumentsEditor label="Documents / Ressources téléchargeables" items={it.documents || []} onChange={(v) => patch(i, { documents: v })} testid={`formation-documents-${i}`} />

        <div className="border-t border-white/10 pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><CalendarDays size={13} /> Sessions</span>
            <button onClick={() => addSession(i)} data-testid={`admin-add-session-${i}`} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter une session</button>
          </div>
          {(it.sessions || []).length === 0 && <p className="text-xs text-secondary2">Aucune session programmée.</p>}
          {(it.sessions || []).map((s, si) => {
            const rem = remaining(s);
            return (
              <div key={s.id || si} className="rounded border border-white/10 bg-[#050505] p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] uppercase tracking-[0.2em] ${rem === 0 ? "text-red-400" : "text-electric"}`}>{rem === 0 ? "Complet" : `${rem} ${value.spotsSuffix || "places restantes"}`}</span>
                  <button onClick={() => setSessions(i, it.sessions.filter((_, x) => x !== si))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Input label="Date de début" value={s.date} onChange={(v) => patchSession(i, si, { date: v })} placeholder="AAAA-MM-JJ ou 14 novembre 2026" />
                  <Input label="Heure de début" value={s.time} onChange={(v) => patchSession(i, si, { time: v })} placeholder="14:00" />
                  <Input label="Date de fin (si plusieurs jours)" value={s.endDate} onChange={(v) => patchSession(i, si, { endDate: v })} placeholder="vide = même jour" />
                  <Input label="Heure de fin" value={s.endTime} onChange={(v) => patchSession(i, si, { endTime: v })} placeholder="17:00" />
                  <Input label="Nom du lieu" value={s.location} onChange={(v) => patchSession(i, si, { location: v })} />
                  <Input label="Adresse complète (📍 Maps/Waze)" value={s.address} onChange={(v) => patchSession(i, si, { address: v })} />
                  <Input label="Places totales" value={s.totalSpots} onChange={(v) => patchSession(i, si, { totalSpots: v })} />
                  <Input label="Places confirmées (auto = candidatures acceptées)" value={s.takenSpots} onChange={(v) => patchSession(i, si, { takenSpots: v })} />
                </div>
                <label className="flex items-center gap-2 text-xs text-secondary2">
                  <input type="checkbox" checked={!!s.allDay} onChange={(e) => patchSession(i, si, { allDay: e.target.checked })} className="accent-electric" data-testid={`session-allday-${si}`} />
                  Toute la journée (masque les heures sur le site)
                </label>
              </div>
            );
          })}
        </div>

        <QuestionsEditor items={it.questions || []} onChange={(v) => patch(i, { questions: v })} testid={`formation-questions-${i}`} />

        {/* Gestion des candidatures de cette formation */}
        <div className="border-t border-white/10 pt-4" data-testid="formation-candidacies">
          <CandidaciesPanel refId={it.id} refTitle={it.title} sessions={it.sessions || []}
            defaultSession={(it.sessions || [])[0]?.id || ""} />
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-formations-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Sur-titre" value={value.eyebrow} onChange={(v) => setField("eyebrow", v)} />
        <Input label="Titre de section" value={value.heading} onChange={(v) => setField("heading", v)} />
      </div>
      <Area label="Introduction" value={value.intro} onChange={(v) => setField("intro", v)} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Libellé « Complet »" value={value.fullLabel} onChange={(v) => setField("fullLabel", v)} />
        <Input label="Suffixe places (ex: places restantes)" value={value.spotsSuffix} onChange={(v) => setField("spotsSuffix", v)} />
        <Input label="Libellé bouton (ex: S'inscrire)" value={value.ctaLabel} onChange={(v) => setField("ctaLabel", v)} />
      </div>
      <Input label="Libellé « Bientôt disponible » (bouton verrouillé)" value={value.comingLabel} onChange={(v) => setField("comingLabel", v)} placeholder="Bientôt disponible" />

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Formations & ateliers ({items.length})</span>
        <button onClick={add} data-testid="admin-add-formation" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter une formation</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="formation-fiche" title={items[open].title} subtitle="Formation / Atelier" onClose={() => setOpen(null)}>
          {renderForm(open)}
        </FicheOverlay>
      ) : (
        <>
          {items.length === 0 && <p className="text-secondary2 text-sm">Aucune formation. Cliquez sur « Ajouter une formation ».</p>}
          <CardGrid testid="formations-cards">
            {items.map((it, i) => (
              <ItemCard key={it.id || i} testid={`admin-formation-item-${i}`}
                image={it.image} frame={it.imageFrame}
                title={it.title} subtitle={[it.level, it.duration, it.price].filter(Boolean).join(" · ")}
                dimmed={it.status === "hidden"}
                badges={[{ label: { draft: "Brouillon", coming: "Bientôt disponible", available: "Disponible" }[effectiveState(it)] || "Disponible", tone: "text-electric border-electric/40 bg-black/60" }]}
                onOpen={() => setOpen(i)}
                actions={
                  <>
                    <button onClick={() => duplicate(i)} data-testid={`admin-duplicate-formation-${i}`} title="Dupliquer cette formation" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><Copy size={14} /></button>
                    <button onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                    <button onClick={() => setItems(items.filter((_, x) => x !== i))} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                  </>
                } />
            ))}
          </CardGrid>
        </>
      )}
    </div>
  );
};
